import { Button, DatePicker, Radio, RadioGroup, Select, Typography } from "@douyinfe/semi-ui";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { useEffect, useMemo, useState } from "react";
import { ApiClient } from "../../../api/ApiClient";
import { DashboardFilters } from "../api/homeDashboard";

dayjs.extend(isoWeek);

interface TeamOption { id: number; name: string; }
interface UserOption { id: number; name: string; teamId?: number; }

interface Paged<T> { records: T[]; }

interface Props {
  value: DashboardFilters;
  onChange: (f: DashboardFilters) => void;
  onApply: () => void;
  onReset: () => void;
  isAdmin: boolean;
  currentTeamId?: number;
}

const api = ApiClient.getDefault();

function detectQuickKey(value: DashboardFilters): string {
  if (!value.startDate || !value.endDate) return "";
  const today = dayjs();
  const keys: Array<["weekNatural" | "monthNatural" | "rolling7" | "rolling30", dayjs.Dayjs]> = [
    ["weekNatural", today.startOf("isoWeek")],
    ["monthNatural", today.startOf("month")],
    ["rolling7", today.subtract(6, "day")],
    ["rolling30", today.subtract(29, "day")],
  ];
  for (const [k, start] of keys) {
    if (dayjs(value.startDate).isSame(start, "day") && dayjs(value.endDate).isSame(today, "day")) {
      return k;
    }
  }
  return "";
}

export default function FilterBar({ value, onChange, onApply, onReset, isAdmin, currentTeamId }: Props) {
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [sales, setSales] = useState<UserOption[]>([]);

  useEffect(() => {
    if (isAdmin) {
      api.request<Paged<TeamOption>>("/teams", "GET", { page: 0, size: 200 })
        .then((r) => setTeams(r.data?.records || []))
        .catch(() => setTeams([]));
    } else {
      setTeams([]);
    }
  }, [isAdmin]);

  useEffect(() => {
    api.request<UserOption[]>("/users/sales", "GET")
      .then((r) => setSales(r.data || []))
      .catch(() => setSales([]));
  }, []);

  const effectiveTeamId = isAdmin ? value.teamId : currentTeamId;
  const users = effectiveTeamId
    ? sales.filter(u => u.teamId === effectiveTeamId)
    : sales;

  const quickValue = useMemo(() => detectQuickKey(value), [value.startDate, value.endDate]);

  const applyQuick = (key: string) => {
    const today = dayjs();
    let start: dayjs.Dayjs;
    switch (key) {
      case "weekNatural": start = today.startOf("isoWeek"); break;
      case "monthNatural": start = today.startOf("month"); break;
      case "rolling7": start = today.subtract(6, "day"); break;
      case "rolling30": start = today.subtract(29, "day"); break;
      default: return;
    }
    onChange({ ...value, startDate: start.format("YYYY-MM-DD"), endDate: today.format("YYYY-MM-DD") });
  };

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "6px 6px", background: "var(--semi-color-bg-1)",
      borderRadius: 6, marginBottom: 0, flexWrap: "wrap",
    }}>
      <Typography.Text strong>日期区间：</Typography.Text>
      <DatePicker type="dateRange" style={{width:250}} value={[value.startDate as any, value.endDate as any]}
        onChange={(v: any) => {
          if (v && v.length === 2) {
            onChange({ ...value, startDate: dayjs(v[0]).format("YYYY-MM-DD"), endDate: dayjs(v[1]).format("YYYY-MM-DD") });
          }
        }} />
      <RadioGroup value={quickValue} onChange={(e) => applyQuick(e.target.value)}>
        <Radio value="weekNatural">本周(自然)</Radio>
        <Radio value="monthNatural">本月(自然)</Radio>
        <Radio value="rolling7">滚动7天</Radio>
        <Radio value="rolling30">滚动30天</Radio>
      </RadioGroup>
      {isAdmin && (
        <Select style={{ width: 160 }} placeholder="团队" value={value.teamId}
          onChange={(v) => onChange({ ...value, teamId: v as number, saleIds: [] })}
          optionList={teams.map(t => ({ value: t.id, label: t.name }))} />
      )}
      <Select style={{ width: 180 }} multiple placeholder="团队成员(空=全部)" value={value.saleIds}
        onChange={(v) => onChange({ ...value, saleIds: v as number[] })}
        optionList={users.map(u => ({ value: u.id, label: u.name }))} />
      <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
        <Button onClick={onReset}>重置</Button>
        <Button theme="solid" type="primary" onClick={onApply}>查询</Button>
      </div>
    </div>
  );
}
